import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Modal, Spinner } from 'react-bootstrap';
import DSOSidebar from '../../components/DSOSidebar';
import { useAuth } from '../../hooks/useAuth';
import { searchApi, notificationApi } from '../../services/api';
import SearchResults from '../../components/SearchResults';
import './Dashboard.css';

// Import icons
import { FaSignOutAlt, FaUser, FaSearch, FaTimes } from 'react-icons/fa';

const DSODashboard = () => {
  const navigate = useNavigate();
  const { logout, getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();

  // --- Notification State ---
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false); // Controls dropdown visibility
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  // --- End Notification State ---
  
  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchRef = useRef(null);
  // --- End Search State ---
      
  // Set document title on mount
  useEffect(() => {
    document.title = 'DSO Dashboard';
  }, []); // Empty dependency array
  
  // Handle search input changes with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.trim().length >= 2) {
      // Set a new timeout to perform the search
      const timeoutId = setTimeout(() => {
        performSearch(query);
      }, 300); // 300ms debounce
      
      setSearchTimeout(timeoutId);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };
  
  // Perform the search
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle search result click
  const handleResultClick = () => {
    setShowSearchResults(false);
    setShowSearchModal(false);
    setSearchQuery('');
  };
  
  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchRef]);

  // --- Notification useEffect Hooks ---
  // Fetch unread count periodically
  useEffect(() => {
    let intervalId;
    const fetchNotificationsCount = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
        // Don't set error state here to avoid disrupting the main dashboard view
      }
    };

    fetchNotificationsCount(); // Initial fetch
    intervalId = setInterval(fetchNotificationsCount, 60000); // Fetch every 60 seconds

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, []);

  // Fetch full notifications when dropdown is opened
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          // Fetch latest 10 notifications
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications for dropdown:', error);
          setNotifications([]); // Clear notifications on error
        } finally {
          setLoadingNotifications(false);
        }
      }
    };

    fetchFullNotifications();
  }, [showNotifications]); // Re-run when showNotifications changes
  // --- End Notification useEffect Hooks ---
      
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // --- Notification Helper Functions ---
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    // Only proceed if the notification exists and is unread
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      // Update local state optimistically
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      // Decrement count optimistically ONLY if it was unread
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Optionally show an error message to the user
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return; // No unread notifications

    try {
      await notificationApi.markAllAsRead();
      // Update local state optimistically
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0); // Reset count
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Optionally show an error message
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      // Use more user-friendly formatting
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) {
      console.error("Error formatting timestamp:", e);
      return timestamp; // Fallback to original string
    }
  };
  // --- End Notification Helper Functions ---

  return (
    <div className="dso-dashboard-layout">
      <DSOSidebar />
      
      <div className="dso-content-wrapper">
        <header className="dso-header">
          <div className="dso-greeting">
            <span className="dso-greeting-text">Hi, {currentUser?.name || 'DSO'} <span className="dso-greeting-emoji">👋</span></span>
          </div>
          <div className="dso-header-search" ref={searchRef}>
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search Agents, Leads, Backoffice..." 
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={() => {
                  if (searchQuery.trim().length >= 2) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
              {searchQuery && !isSearching && (
                <FaTimes 
                  className="search-clear-icon" 
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                    setSearchResults([]);
                  }} 
                />
              )}
            </div>
            
            {/* Desktop Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-container">
                <SearchResults results={searchResults} onResultClick={handleResultClick} />
              </div>
            )}
            
            {/* Mobile Search Button */}
            <div className="mobile-search-button" onClick={() => setShowSearchModal(true)}>
              <FaSearch />
            </div>
          </div>
          
          <div className="dso-header-actions">

            <div className="dso-user-profile">
              <div className="dso-avatar">
                <FaUser />
              </div>
              <div className="dso-user-info">
                <span className="dso-user-name">
                  {currentUser?.name || 'DSO User'}
                </span>
                <button className="dso-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        <div className="dso-main-content">
          <Outlet />
        </div>
      </div>
      
      {/* Mobile Search Modal */}
      <Modal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        centered
        className="search-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Search</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mobile-search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search DSOs, agents, leads..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
            />
            {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
            {searchQuery && !isSearching && (
              <FaTimes
                className="search-clear-icon"
                onClick={() => {
                  setSearchQuery('');
                  setShowSearchResults(false);
                  setSearchResults([]);
                }}
              />
            )}
          </div>
          
          {searchResults.length > 0 && (
            <div className="mobile-search-results">
              <SearchResults results={searchResults} onResultClick={handleResultClick} />
            </div>
          )}
          
          {searchQuery.trim().length >= 2 && searchResults.length === 0 && !isSearching && (
            <div className="no-results-message">
              No results found for "{searchQuery}"
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DSODashboard;
